import { useCallback, useEffect, useMemo, useState } from "react";
import CompositionBar from "../CompositionBar.jsx";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { boundedLimit } from "../../lib/decisionScales.js";
import { colorForKey, KernelEmpty } from "./shared.jsx";

export const kind = "shareBar";

function normalizeTabs(data, maxItems) {
    const rawTabs = Array.isArray(data) && data.some((entry) => Array.isArray(entry?.segments))
        ? data
        : [{ id: "all", label: null, segments: Array.isArray(data) ? data : [] }];
    return rawTabs.flatMap((tab, tabIndex) => {
        const source = Array.isArray(tab?.segments) ? tab.segments : [];
        const shaped = source
            .map((segment, index) => {
                const focusValue = String(segment?.key ?? segment?.label ?? index);
                return {
                    renderKey: `segment:${index}:${focusValue}`,
                    focusValue,
                    folded: false,
                    label: String(segment?.label ?? segment?.key ?? "Unlabelled"),
                    color: sanitizeRenderColor(segment?.color, colorForKey(focusValue)),
                    count: asNumber(segment?.count ?? segment?.value),
                };
            })
            .filter((segment) => segment.count != null && segment.count > 0)
            .sort((a, b) => b.count - a.count);
        const kept = shaped.slice(0, maxItems);
        const tail = shaped.slice(maxItems);
        if (tail.length) {
            kept.push({
                renderKey: "folded-tail",
                focusValue: null,
                folded: true,
                label: `Other (${tail.length})`,
                color: "var(--muted)",
                count: tail.reduce((sum, segment) => sum + segment.count, 0),
            });
        }
        if (!kept.length) return [];
        const total = kept.reduce((sum, segment) => sum + segment.count, 0) || 1;
        return [{
            id: String(tab?.id ?? tabIndex),
            label: tab?.label == null ? null : String(tab.label),
            field: tab?.field || null,
            lede: tab?.lede || null,
            segments: kept.map((segment) => ({ ...segment, share: segment.count / total })),
        }];
    });
}

export function Component({ spec, data, fillHeight = false, onFocus }) {
    const maxItems = boundedLimit(spec.maxItems, 12, 20, 2);
    const tabs = useMemo(() => normalizeTabs(data, maxItems), [data, maxItems]);
    const [tabId, setTabId] = useState(tabs[0]?.id || null);
    const active = tabs.find((tab) => tab.id === tabId) || tabs[0] || null;
    const segmentKey = active ? `${active.id}:${active.segments.map((segment) => segment.renderKey).join("|")}` : "";
    const [hidden, setHidden] = useState(() => new Set());
    useEffect(() => setHidden(new Set()), [segmentKey]);
    const toggle = useCallback((key) => setHidden((previous) => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key);
        else if (previous.size < (active?.segments.length || 0) - 1) next.add(key);
        return next;
    }), [active?.segments.length]);

    if (!active) return <KernelEmpty message={spec.empty || "No distribution to show"} />;
    const visible = active.segments.filter((segment) => !hidden.has(segment.renderKey));
    const visibleTotal = visible.reduce((sum, segment) => sum + segment.count, 0) || 1;
    const rendered = visible.map((segment) => ({
        ...segment,
        key: segment.renderKey,
        share: segment.count / visibleTotal,
    }));
    const format = spec.format || "number";

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {spec.title ? (
                <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">{spec.title}</div>
            ) : null}
            {tabs.length > 1 ? (
                <div className="mb-[6px] flex flex-wrap gap-[2px]">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setTabId(tab.id)}
                            aria-pressed={tab.id === active.id}
                            className={`border px-[8px] py-[3px] text-[10px] ${tab.id === active.id ? "border-[color:var(--line-strong)] text-[color:var(--ink)]" : "border-transparent text-[color:var(--muted)]"}`}
                        >
                            {tab.label || tab.id}
                        </button>
                    ))}
                </div>
            ) : null}
            {active.lede ? <div className="mb-[7px] text-[11px] leading-[1.35] text-[color:var(--muted)]">{active.lede}</div> : null}
            <div style={{ height: `${boundedLimit(spec.height, 42, 96, 20)}px` }}>
                <CompositionBar segments={rendered} />
            </div>
            <div className="mt-[5px] flex flex-wrap gap-[3px]">
                {active.segments.map((segment) => {
                    const shown = !hidden.has(segment.renderKey);
                    return (
                        <button
                            key={segment.renderKey}
                            type="button"
                            onClick={(event) => {
                                if (event.shiftKey && onFocus && active.field && !segment.folded) {
                                    onFocus(active.field, segment.focusValue);
                                    return;
                                }
                                toggle(segment.renderKey);
                            }}
                            aria-label={`${shown ? "Hide" : "Show"} ${segment.label}`}
                            aria-pressed={shown}
                            className={`inline-flex items-center gap-[4px] border px-[5px] py-[2px] text-[9.5px] ${shown ? "border-transparent text-[color:var(--muted)]" : "border-[color:var(--line)] text-[color:var(--muted-soft)] line-through"}`}
                        >
                            <span className="h-[6px] w-[6px]" style={{ background: shown ? sanitizeRenderColor(segment.color) : "transparent" }} aria-hidden="true" />
                            {segment.label}
                            <span className="font-mono tabular-nums text-[color:var(--muted-soft)]">{formatValue(segment.count, format)}</span>
                        </button>
                    );
                })}
                {hidden.size ? (
                    <button type="button" onClick={() => setHidden(new Set())} className="border border-[color:var(--line)] px-[5px] py-[2px] text-[9.5px] text-[color:var(--muted)]">Show all</button>
                ) : null}
            </div>
            {onFocus && active.field ? (
                <div className="mt-[4px] text-[9.5px] text-[color:var(--muted-soft)]">Shift-click a category to focus the canvas.</div>
            ) : null}
        </div>
    );
}
